import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CheckInInfo,
  ClockInInfo,
  CustomerInfo,
  FieldAutomationConfig,
  FormattedLocation,
} from './trip-model';
import { RootState } from '../root/root-types';
import { isEmpty, isUndefined } from 'lodash';

export const TRIP_FEATURE_KEY = 'trip';

export interface TripState {
  clockInInfo: ClockInInfo | undefined;
  checkInInfo: CheckInInfo | undefined;
  fieldAutomationConfig: FieldAutomationConfig;
  customerInfo: CustomerInfo | undefined;
  beatPlanScheduleIds: {
    beatPlanScheduleCustomerId: string | null;
    beatPlanScheduleRouteId: string | null;
  };
  currentCustomerLocation: FormattedLocation | undefined;
}

export const initialTripState: TripState = {
  clockInInfo: undefined,
  fieldAutomationConfig: {
    enableGeoTagging: false,
    geoFencingRadius: 100,
    disableRetailingWithoutCheckIn: false,
  },
  beatPlanScheduleIds: {
    beatPlanScheduleCustomerId: null,
    beatPlanScheduleRouteId: null,
  },
  checkInInfo: undefined,
  customerInfo: undefined,
  currentCustomerLocation: {},
};

export const tripSlice = createSlice({
  name: TRIP_FEATURE_KEY,
  initialState: initialTripState,
  reducers: {
    updateClockInInfo(state, action: PayloadAction<ClockInInfo | undefined>) {
      if (action.payload?.clockIn) {
        state.clockInInfo = action.payload;
      } else {
        state.clockInInfo = undefined;
      }
    },
    updateFieldAutomationConfig(
      state,
      action: PayloadAction<Partial<FieldAutomationConfig>>
    ) {
      const oldConfig = state.fieldAutomationConfig || {
        enableGeoTagging: false,
        geoFencingRadius: 100,
        disableRetailingWithoutCheckIn: false,
      };
      state.fieldAutomationConfig = {
        ...oldConfig,
        ...action.payload,
      };
    },
    updateCustomerInfo(state, action: PayloadAction<CustomerInfo | undefined>) {
      state.customerInfo = action.payload;
    },
    updateBeatPlanScheduleIds(
      state,
      action: PayloadAction<{
        beatPlanScheduleCustomerId: string | null;
        beatPlanScheduleRouteId: string | null;
      }>
    ) {
      state.beatPlanScheduleIds = action.payload;
    },
    updateCheckInInfo(state, action: PayloadAction<CheckInInfo | undefined>) {
      if (action.payload?.checkInId || action.payload?.checkInTime) {
        state.checkInInfo = action.payload;
      } else {
        state.checkInInfo = undefined;
      }
    },
    updateCurrentCustomerLocation(
      state,
      action: PayloadAction<FormattedLocation | undefined>
    ) {
      if (isUndefined(action.payload)) {
        state.currentCustomerLocation = undefined;
      } else {
        state.currentCustomerLocation = action.payload;
      }
    },
  },
});

export const tripReducer = tripSlice.reducer;

export const {
  updateClockInInfo,
  updateFieldAutomationConfig,
  updateBeatPlanScheduleIds,
  updateCheckInInfo,
  updateCustomerInfo,
  updateCurrentCustomerLocation,
} = tripSlice.actions;

export const getTripState = (rootState: RootState): TripState =>
  rootState[TRIP_FEATURE_KEY];

export const getClockInInfo = createSelector(
  getTripState,
  (state) => state.clockInInfo
);

export const getCheckInInfo = createSelector(
  getTripState,
  (state) => state.checkInInfo
);

export const getFieldAutomationConfig = createSelector(
  getTripState,
  (state) => state.fieldAutomationConfig
);

export const getCustomerInfo = createSelector(
  getTripState,
  (state) => state.customerInfo
);

export const getBeatPlanScheduleIds = createSelector(
  getTripState,
  (state) => state.beatPlanScheduleIds
);

export const getCurrentCustomerLocation = createSelector(
  getTripState,
  (state) => state.currentCustomerLocation
);
